import { useCallback, useMemo } from "react";
import {
  DataTable,
  DataTableCellContent,
  DataTableEmpty,
  DataTableToolbar,
  DataTableToolbarLeft,
  DataTableToolbarActions,
  Badge,
  Box,
  Checkbox,
  Button,
  Text,
  Icon,
  MenuItem,
  MenuSeparator,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  createColumnHelper,
  renderDefaultGroupActions,
  type DataTableColumnDef,
  type DataTableGroupType,
  type BulkAction,
  type SortingState,
  type SurfaceToken,
  type OnChangeFn,
} from "@salestrak/ui";
import type { ReactNode } from "react";

import { ViewSearch } from "#app/components/filters/view-search";
import { usePermission } from "#app/lib/hooks/use-permission";
import { useTableViewSettings } from "#app/lib/hooks/use-table-view-settings";
import {
  type CustomFieldRow,
  CustomFieldsListDataDocument,
} from "#app/lib/data";
import { EntityTag } from "#app/components/entity";
import { getFieldTypeMeta } from "#app/lib/constants/field-type-icons";
import { DateText } from "#app/components/date-text";
import { useFieldTypeOrder } from "#app/lib/hooks/use-field-type-order";
import { useFeatureFlags, FeatureFlagKey } from "#app/hooks/use-feature-flags";
import {
  GroupQueryProvider,
  useGroupStates,
} from "#app/lib/hooks/group-query-provider";
import type { Omittable } from "@salestrak/ui";

// The bulk bar's count-chip noun: "1 Field selected".
const FIELD_SELECTION_NOUN = { one: "Field", other: "Fields" };

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

const h = createColumnHelper<CustomFieldRow>();

// Shared by the table (sticky pinning) and the column settings panel (locked
// rows). The panel ignores structural ids like "select".
const CUSTOM_FIELDS_PINNED_COLUMN_IDS = ["select", "name"];

// Icon-only Type cell: the type's glyph in its key color, with the type name
// in a hover tooltip. Touch has no hover, so the bare glyph stands alone
// there; grouped mode's type bands name it anyway.
function FieldTypeIconCell({ type }: { type: string }) {
  const meta = getFieldTypeMeta(type);
  return (
    <Tooltip>
      <TooltipTrigger
        render={<Icon name={meta.icon} size="md" color={meta.color} />}
      />
      <TooltipPopup side="top">{meta.label}</TooltipPopup>
    </Tooltip>
  );
}

// The hover Edit chip on the name cell — same grammar as the issues table's
// rename pencil: an extra hover-revealed action ahead of the cell's own
// press target, self-gated (an in-cell affordance HIDES without the grant;
// only menu rows disable with a tooltip). The name press already opens the
// editor; this chip is the visible affordance saying so.
function CustomFieldEditAction({
  field,
  onEdit,
}: {
  field: CustomFieldRow;
  onEdit: (field: CustomFieldRow) => void;
}) {
  const canManage = usePermission("custom_fields.manage");
  if (!canManage) return null;
  return (
    <Button
      variant="outline"
      size="sm"
      aria-label={`Edit ${field.name}`}
      onClick={(e: React.MouseEvent) => {
        // The hosting cell opens the editor on click too; the chip press
        // must not double-fire it.
        e.stopPropagation();
        onEdit(field);
      }}
    >
      Edit
      <Icon name="panel-right" color="muted" />
    </Button>
  );
}

/** Picker mode: the table reads as a checklist over the fields a location
 *  can still take — the host's `where` excludes the ones it already carries,
 *  so every row here is pickable. `selectedIds` is the host's draft of new
 *  picks. The ROW owns the press (the table's onRowClick), so the checkbox is
 *  a state glyph, not a second hit target. */
export type CustomFieldsTableSelection = {
  selectedIds: ReadonlySet<string>;
  onToggle: (field: CustomFieldRow) => void;
};

function SelectionCheckboxCell({
  field,
  selection,
}: {
  field: CustomFieldRow;
  selection: CustomFieldsTableSelection;
}) {
  return (
    <DataTableCellContent>
      <Checkbox
        checked={selection.selectedIds.has(field.customFieldId)}
        className="pointer-events-none"
      />
    </DataTableCellContent>
  );
}

// Duplicate and delete are the row-action menu's, not the columns' — the menu
// closes over them where it is rendered.
function useCustomFieldColumns({
  onEdit,
  selection,
}: Omittable<{
  onEdit?: (field: CustomFieldRow) => void;
  selection?: CustomFieldsTableSelection;
}>): DataTableColumnDef<CustomFieldRow>[] {
  return useMemo<DataTableColumnDef<CustomFieldRow>[]>(
    () => [
      ...(selection
        ? [
            h.display({
              id: "select",
              header: "",
              enableSorting: false,
              // The FIRST column absorbs the table's 24px edge inset plus the
              // cell's own 12px leading padding before content starts — under
              // ~56px the checkbox is clipped beneath the sticky name lane.
              meta: { width: 56 },
              cell: ({ row }) => (
                <SelectionCheckboxCell
                  field={row.original}
                  selection={selection}
                />
              ),
            }),
          ]
        : []),
      // Picker mode strips the name cell to plain text: the row press toggles
      // the checkbox, so an Edit hover action would fight it.
      selection || !onEdit
        ? h.accessor({
            accessorKey: "name",
            header: "Name",
            enableSorting: true,
            meta: { width: 320 },
            cell: ({ row }) => (
              <DataTableCellContent>
                <Text size="sm" weight="medium" truncate={1}>
                  {row.original.name}
                </Text>
              </DataTableCellContent>
            ),
          })
        : h.hoverAction({
            accessorKey: "name",
            header: "Name",
            enableSorting: true,
            meta: { width: 320 },
            content: (field) => (
              <Text size="sm" weight="medium" truncate={1}>
                {field.name}
              </Text>
            ),
            onClickAction: (field) => onEdit(field),
            actionLabel: (field) => `Edit ${field.name}`,
            actions: (field) => (
              <CustomFieldEditAction field={field} onEdit={onEdit} />
            ),
          }),
      h.display({
        id: "type",
        header: "Type",
        enableSorting: false,
        meta: { minWidth: 64 },
        cell: ({ row }) => (
          <DataTableCellContent>
            <FieldTypeIconCell type={row.original.type} />
          </DataTableCellContent>
        ),
      }),
      h.display({
        id: "createdBy",
        header: "Created By",
        enableSorting: false,
        meta: { minWidth: 180 },
        cell: ({ row }) => {
          const user = row.original.createdBy;
          if (!user) {
            return (
              <DataTableCellContent>
                <Text size="sm" variant="muted">Unknown</Text>
              </DataTableCellContent>
            );
          }
          return (
            // A cell never labels a member: the avatar alone, hovering to
            // the member preview that carries the name. Read-only, so no ring.
            <DataTableCellContent>
              <EntityTag
                entity={{ kind: "user", id: user.userId, user }}
                display="visual"
                ghost={false}
              />
            </DataTableCellContent>
          );
        },
      }),
      // Date columns are sortable (server orderBy), but rendered with the
      // tag manager's recency grammar — relative label, full timestamp in a
      // tooltip — instead of the library column.date's plain calendar date.
      h.accessor({
        accessorKey: "creationDate",
        header: "Date Created",
        enableSorting: true,
        meta: { minWidth: 120, textBrightness: "subtle" },
        cell: ({ row }) => (
          <DataTableCellContent>
            <DateText date={row.original.creationDate} />
          </DataTableCellContent>
        ),
      }),
      h.accessor({
        accessorKey: "lastUpdateDate",
        header: "Updated",
        enableSorting: true,
        meta: { minWidth: 120, textBrightness: "subtle" },
        cell: ({ row }) => (
          <DataTableCellContent>
            <DateText date={row.original.lastUpdateDate} />
          </DataTableCellContent>
        ),
      }),
    ],
    [onEdit, selection],
  );
}

// "Updated" duplicates "Date Created" for a never-edited field, so it hides
// until the user shows it (persisted via the table view's `shown` list).
const CUSTOM_FIELDS_DEFAULT_HIDDEN_COLUMN_IDS = ["lastUpdateDate"];

// ---------------------------------------------------------------------------
// Per-group query helpers
// ---------------------------------------------------------------------------

const getConnection = (data: any) => data?.customFields;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type CustomFieldsTableProps = Omittable<{
  where: Record<string, unknown>;
  // Undefined when the list defers ordering to the API (`deferOrderBy`) and the
  // user has not sorted — the variable is then simply omitted.
  orderBy: Record<string, string> | undefined;
  groupByField?: "none" | "type";
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  searchValue: string;
  onSearchChange: (value: string) => void;
  /** Picker mode — the add-fields dialog: a leading checkbox column reads
   *  membership + draft picks, the row press toggles, and the manage
   *  affordances (edit chip, row menu, column picker) all stand down. */
  selection?: CustomFieldsTableSelection;
  onEdit?: (field: CustomFieldRow) => void;
  onDuplicate?: (field: CustomFieldRow) => void;
  // Absent when the viewer lacks the delete.items grant — deleting a field
  // destroys its stored values on every item, so the affordance (row menu
  // entry, and the caller's bulk bar / danger zone) hides entirely.
  onDelete?: (field: CustomFieldRow) => void;
  bulkActions?: BulkAction<CustomFieldRow>[];
  /** Per-group "+ Create …" footer row (see DataTable.groupFooter) — the
   *  settings page wires its field-type selector around a DataTableAddRow;
   *  the manage dialog renders a per-type "Create <type> field" row that
   *  opens the create form pre-set to the group's type. */
  groupFooter?: (groupKey: string) => ReactNode;
  toolbarLeft?: ReactNode;
  toolbarActions?: ReactNode;
  /** What an empty, unsearched list says. The default names the workspace
   *  ("No custom fields yet"); a host whose `where` narrows the set — the
   *  add-fields picker — says what ITS empty means instead. */
  emptyMessage?: string;
  // The surface the table sits on. Default `background` — which both hosts
  // now are: the settings page natively, the manage dialog via its
  // bg-background body override.
  surface?: SurfaceToken;
}>;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CustomFieldsTable({
  where: whereProp,
  orderBy: orderByProp,
  groupByField = "none",
  sorting,
  onSortingChange,
  searchValue,
  onSearchChange,
  selection,
  onEdit,
  onDuplicate,
  onDelete,
  bulkActions,
  groupFooter,
  toolbarLeft,
  toolbarActions,
  emptyMessage = "No custom fields yet",
  surface,
}: CustomFieldsTableProps) {
  const columns = useCustomFieldColumns({ onEdit, selection });
  const isGrouped = groupByField === "type";
  // The picker persists its own column prefs — sharing the manager's key
  // would let one surface hide a column the other relies on.
  const tableView = useTableViewSettings(
    selection ? "custom-fields-picker" : "custom-fields",
    {
      defaultHiddenColumnIds: CUSTOM_FIELDS_DEFAULT_HIDDEN_COLUMN_IDS,
    },
  );
  // Workspace-scoped Photos flag drops the photos type entirely: no photos
  // group/query when grouped, and photos rows filtered from the flat list (the
  // server `where` can't exclude a type, so the flat path filters client-side).
  const fieldTypeOrder = useFieldTypeOrder();
  const { hasFlag } = useFeatureFlags();
  const photosEnabled = hasFlag(FeatureFlagKey.PHOTOS);

  // --- Group query state ---
  const { states, registerState, getGroupProps } = useGroupStates<CustomFieldRow>();

  // Per-type where clauses — stable references (one per field type)
  const perTypeWhere = useMemo(
    () => Object.fromEntries(fieldTypeOrder.map((type) => [type, { ...whereProp, type: { eq: type } }])),
    [whereProp, fieldTypeOrder],
  );

  // Build table groups based on grouping mode
  const groups = useMemo<DataTableGroupType<CustomFieldRow>[]>(() => {
    if (!isGrouped) {
      const all = getGroupProps("__all__");
      return [{
        key: "__all__",
        label: "All",
        totalCount: states.get("__all__")?.total,
        ...all,
        // The flat query can't exclude photos server-side, so drop them here.
        data: photosEnabled
          ? all.data
          : all.data.filter((f) => f.type !== "photos"),
      }];
    }

    const result: DataTableGroupType<CustomFieldRow>[] = [];
    for (const type of fieldTypeOrder) {
      const state = states.get(type);
      if (state && (state.total > 0 || state.loading)) {
        const meta = getFieldTypeMeta(type);
        result.push({
          key: type,
          label: meta.label,
          totalCount: state.total,
          ...getGroupProps(type),
        });
      }
    }

    // Include any types not in our known order (future-proofing)
    for (const [key, state] of states) {
      if (!fieldTypeOrder.includes(key) && key !== "__all__" && (state.total > 0 || state.loading)) {
        result.push({
          key,
          label: key,
          totalCount: state.total,
          ...getGroupProps(key),
        });
      }
    }

    // Empty state — single fallback group
    if (result.length === 0) {
      return [{ key: "__all__", label: "All", data: [] as CustomFieldRow[], loading: true }];
    }

    return result;
  }, [isGrouped, states, getGroupProps, fieldTypeOrder, photosEnabled]);

  // Type band chip: the type's glyph + label as a soft tinted chip on the
  // group band. `chip` is the labeled-chip step; the label rides at
  // 12px/medium to sit the chip's 24px box.
  const renderGroupTitle = useCallback((groupKey: string) => {
    if (groupKey === "__all__") return null;
    const meta = getFieldTypeMeta(groupKey);
    return (
      <Badge color={meta.color} size="chip">
        <Icon name={meta.icon} size="xs" />
        <Text size="xs" weight="medium">{meta.label}</Text>
      </Badge>
    );
  }, []);

  return (
    <>
      {!isGrouped && (
        <GroupQueryProvider<CustomFieldRow>
          groupKey="__all__"
          document={CustomFieldsListDataDocument}
          variables={{ where: whereProp, orderBy: orderByProp, limit: 50 }}
          getConnection={getConnection}
          onStateChange={registerState}
        />
      )}

      {isGrouped && fieldTypeOrder.map((type) => (
        <GroupQueryProvider<CustomFieldRow>
          key={type}
          groupKey={type}
          document={CustomFieldsListDataDocument}
          variables={{ where: perTypeWhere[type], orderBy: orderByProp, limit: 50 }}
          getConnection={getConnection}
          onStateChange={registerState}
        />
      ))}

      {/* Top pad only — the space below the band is the table's scrollable
          `gutterTop`, so rows pass through it once the list scrolls. */}
      <Box paddingTop="sm">
        <DataTableToolbar>
          <DataTableToolbarLeft>
            {toolbarLeft}
          </DataTableToolbarLeft>
          <DataTableToolbarActions>
            <ViewSearch
              onValueChange={onSearchChange}
              size="xs"
            />
            {toolbarActions}
          </DataTableToolbarActions>
        </DataTableToolbar>
      </Box>

      <Box direction="column" fill="flex">
        <DataTable
          columns={columns}
          groups={groups}
          getRowId={(r) => r.customFieldId}
          sorting={sorting}
          onSortingChange={onSortingChange}
          stickyHeader
          gutterTop="md"
          // Reference/tag-manager read: ONE column header at the very top,
          // type groups as inline bands under it — never a header per group.
          sharedHeader
          // Reference row borders: the group band closes with a hairline and
          // so does every row INCLUDING the last — a single-row group reads
          // as a bordered strip (band line above, row line below), with the
          // borderless "+ Create … field" row hanging under it.
          borders="enclosed"
          // 40px seam between type sections: each section closes with its
          // own "+ Create … field" row, so the gap has to read as "next
          // list", not as one list's row rhythm.
          groupGap="xl"
          // The column header keeps its underline in BOTH layouts: grouped,
          // it seats the hoisted header above the first type band; flat, it
          // anchors the header over the first row.
          density="regular"
          pinnedColumnIds={CUSTOM_FIELDS_PINNED_COLUMN_IDS}
          paddingX="lg"
          surface={surface}
          groupFooter={groupFooter}
          {...tableView.tableProps}
          // Tag-manager grammar: opting into onColumnVisibilityChange puts the
          // circle-plus show/hide picker on the actions-column header — the
          // toolbar gear is gone from this surface. The picker opts out: no
          // actions column at all, its trailing edge is the date.
          onColumnVisibilityChange={
            selection
              ? undefined
              : tableView.settingsProps.onColumnVisibilityChange
          }
          bulkActions={bulkActions}
          bulkSelectionNoun={FIELD_SELECTION_NOUN}
          onRowClick={
            selection ? (row) => selection.onToggle(row.original) : undefined
          }
          renderRowActions={
            selection || !onEdit || !onDuplicate
              ? undefined
              : (field) => (
                  <>
                    <MenuItem onClick={() => onDuplicate(field)}>
                      <Icon name="copy" size="md" color="muted" />
                      <Text size="sm">Duplicate</Text>
                    </MenuItem>
                    <MenuItem onClick={() => onEdit(field)}>
                      <Icon name="pencil" size="md" color="muted" />
                      <Text size="sm">Edit</Text>
                    </MenuItem>
                    {onDelete ? (
                      <>
                        <MenuSeparator />
                        <MenuItem onClick={() => onDelete(field)}>
                          <Icon name="trash" size="md" color="muted" />
                          <Text size="sm">Delete...</Text>
                        </MenuItem>
                      </>
                    ) : null}
                  </>
                )
          }
          groupTitle={isGrouped ? renderGroupTitle : undefined}
          groupActions={isGrouped ? renderDefaultGroupActions : undefined}
          emptyState={
            <DataTableEmpty
              message={
                searchValue
                  ? "No custom fields match your search"
                  : emptyMessage
              }
            />
          }
        />
      </Box>
    </>
  );
}
